import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import "./TransactionDetails.css";
import TransferTraker from './TransferTraker';
import { TransactionsApi } from '../../../../api/common/apiIndex';
import StatusSelector from './StatusSelector';
import { TransactionStatusEnum } from '../TransactionStatus';
import TransactionDetailsPanel from './TransactionDetailsPanel';
import Loader from '../../../common/commonUtils/Loader';

const fmtMoney = (n) =>
  new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

const statusPillClass = (s) => {
  switch (s) {
    case 'PROCESSING': return "bg-warning-subtle text-warning-emphasis border border-warning-subtle";
    case 'READY': return "bg-info-subtle text-info-emphasis border border-info-subtle";
    case 'PAID': return "bg-success-subtle text-success-emphasis border border-success-subtle";
    case 'FAILED':
    case 'CANCELLED': return "bg-danger-subtle text-danger-emphasis border border-danger-subtle";
    default: return "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle";
  }
};

export default function TransactionDetails() {
  const { id: uuId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [stateUpdated, setStateUpdated] = useState(false);
  const [status, setStatus] = useState(TransactionStatusEnum.PROCESSING);
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: transaction,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['transactions', uuId],
    queryFn: ({ queryKey }) => TransactionsApi.getByUuid(queryKey[1]),
    staleTime: 1000 * 60 * 5,
  }, );

  useEffect(() => {
    if (transaction?.status) setStatus(transaction.status);
  }, [transaction?.status]);

  const buildTransactionRequestDto = (tx, updates) => {
    if (!tx) return null;

    const asNum = (v) => {
      if (v === undefined || v === null || v === "") return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const asISO = (v) => (typeof v === "string" && v.trim() ? v.trim().toUpperCase() : undefined);

    const agentId =
      (updates.agentId && updates.agentId !== "" ? Number(updates.agentId) : undefined) ??
      tx.agentId;

    const sendAmount = asNum(updates.sendAmount) ?? tx.sendAmount;
    const receiveAmount = asNum(updates.receiveAmount) ?? tx.receiveAmount;

    const sendCurrency = asISO(updates.sendCurrency) ?? tx.sendCurrency;
    const receiveCurrency = asISO(updates.receiveCurrency) ?? tx.receiveCurrency;

    if (!agentId) return null;
    if (!Number.isFinite(Number(sendAmount)) || !Number.isFinite(Number(receiveAmount))) return null;
    if (!sendCurrency || !receiveCurrency) return null;

    return {
      senderId: tx.senderId,
      recipientId: tx.recipientId,
      agentId,
      creatorId: tx.creatorId,
      sendAmount,
      sendCurrency,
      receiveAmount,
      receiveCurrency,
    };
  };

  const updateStatusMutation = useMutation({
    mutationFn: (newStatus) => TransactionsApi.updateStatus(transaction.id, newStatus),
    onMutate: async (newStatus) => {
      setStateUpdated(true);
      await queryClient.cancelQueries({ queryKey: ['transactions', uuId] });
      await queryClient.cancelQueries({ queryKey: ['transactions'] });

      const prevTx = queryClient.getQueryData(['transactions', uuId]);
      const prevList = queryClient.getQueryData(['transactions']);

      queryClient.setQueryData(['transactions', uuId], (old) => old ? { ...old, status: newStatus } : old);
      queryClient.setQueryData(['transactions'], (old) => {
        if (!old) return old;
        if (Array.isArray(old)) return old.map(t => t.id === transaction.id ? { ...t, status: newStatus } : t);
        if (old?.content) return { ...old, content: old.content.map(t => t.id === transaction.id ? { ...t, status: newStatus } : t) };
        return old;
      });

      setStatus(newStatus);
      return { prevTx, prevList };
    },
    onError: (err, _new, ctx) => {
      if (ctx?.prevTx) queryClient.setQueryData(['transactions', uuId], ctx.prevTx);
      if (ctx?.prevList) queryClient.setQueryData(['transactions'], ctx.prevList);
      const msg = err?.response?.data?.message || err?.message || 'Failed to update status.';
      toast.error(msg);
    },
    onSuccess: (_data, newStatus) => {
      toast.success(`Status updated to: ${newStatus}`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', uuId] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['tx-count'] });
    },
  });

  const updateTransactionMutation = useMutation({
    mutationFn: (dto) => TransactionsApi.update(transaction.id, dto),
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update transaction.';
      toast.error(msg);
    },
    onSuccess: () => {
      toast.success('Transaction updated successfully.');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transactions', uuId] });
      queryClient.invalidateQueries({ queryKey: ['tx-count'] });
      setStateUpdated(true);
      setIsEditing(false);
    },
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: () => TransactionsApi.remove(transaction.id),
    onError: (err) => {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete transaction.';
      toast.error(msg);
    },
    onSuccess: () => {
      toast.success('Transaction deleted successfully.');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['tx-count'] });
      goToTransactionDashboard();
    },
  });

  const handleStatusRequest = (newStatus) => {
    if (!transaction?.id) return;

    if (newStatus === TransactionStatusEnum.PAID) {
      toast.dismiss();
      toast(
        ({ closeToast }) => (
          <div>
            <p className="mb-2">
              Mark this transfer as <strong>PAID</strong>?<br />
              <strong>{transaction.senderName}</strong> → <strong>{transaction.recipientName}</strong><br />
              {fmtMoney(transaction.sendAmount)} {transaction.sendCurrency}
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-sm btn-secondary" onClick={closeToast}>Cancel</button>
              <button
                className="btn btn-sm btn-success"
                onClick={() => {
                  closeToast();
                  updateStatusMutation.mutate(newStatus);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        ),
        { autoClose: false, closeOnClick: false, draggable: false, position: "top-center" }
      );
      return;
    }

    if (newStatus !== status) updateStatusMutation.mutate(newStatus);
  };

  const handleDeleteRequest = () => {
    toast.dismiss();
    toast(
      ({ closeToast }) => (
        <div>
          <p className="mb-2">
            Delete transaction?<br />
            <strong>{transaction.senderName}</strong> → <strong>{transaction.recipientName}</strong><br />
            {fmtMoney(transaction.sendAmount)} {transaction.sendCurrency}
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-sm btn-secondary" onClick={closeToast}>Cancel</button>
            <button
              className="btn btn-sm btn-danger"
              onClick={() => {
                closeToast();
                deleteTransactionMutation.mutate();
              }}
            >
              Delete
            </button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false, draggable: false, position: "top-center" }
    );
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!transaction) return;

    const formData = new FormData(e.target);
    const updates = Object.fromEntries(formData.entries());
    const dto = buildTransactionRequestDto(transaction, updates);

    if (!dto) {
      toast.error('Please complete the form (agent required, valid amounts/currencies).');
      return;
    }

    updateTransactionMutation.mutate(dto);
  };

  const goToTransactionDashboard = () => {
    if (!transaction?.id) {
      navigate("/home/transactions");
      return;
    }
    navigate("/home/transactions", {
      state: stateUpdated ? { updatedId: transaction.id } : undefined,
    });
  };

  const sentAt = useMemo(() => {
    if (!transaction?.createdAt) return '—';
    return new Date(transaction.createdAt).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit'
    });
  }, [transaction?.createdAt]);

  const handleCopy = useCallback((text) => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text)).then(() => toast.info('Copied!'));
  }, []);

  const busy =
    updateStatusMutation.isLoading ||
    deleteTransactionMutation.isLoading ||
    updateTransactionMutation.isLoading;

  return (
    <div className="container p-0">
      <div className="d-flex justify-content-end align-items-center mb-3 gap-2 flex-wrap">
        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill"
          onClick={goToTransactionDashboard}
          disabled={busy}
        >
          Dashboard
        </button>
      </div>

      {isLoading && !transaction && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Loader text="Looding..."  />
        </div>
      )}

      {!isLoading && isError && (
        <div className="alert alert-danger" role="alert"> {error?.response?.data?.message || 'Failed to load transaction'}
        </div>
      )}

      {!isLoading && !isError && !transaction && (
        <div className="alert alert-warning">Transaction not found.</div>
      )}

      {!isLoading && !isError && transaction && (
        <div className="card border shadow rounded-4 overflow-hidden">
          <div
            className="p-3"
            style={{ background: 'linear-gradient(180deg, var(--bs-tertiary-bg), var(--bs-body-bg))' }}
          >
            <div className="d-flex flex-column align-items-center text-center gap-2">
              <div className="d-flex align-items-center gap-2">
                {transaction.recipientCountry && (
                  <span
                    className="d-inline-flex align-items-center justify-content-center "
                    style={{ width: 36, height: 36, overflow: "hidden" }}
                    title={transaction.recipientCountry || "Country"}
                  >
                    <img
                      src={`https://flagcdn.com/36x27/${transaction.recipientCountry.toLowerCase()}.png`}
                      alt={transaction.recipientCountry || "Country"}
                      width={36}
                      height={27}
                      loading="lazy"
                      style={{ objectFit: "cover" }}
                    />
                  </span>
                )}
                <h4 className="mb-0 fw-bold">{transaction.recipientName}</h4>
                <span className={`badge rounded-pill ${statusPillClass(status)}`}>{status}</span>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="badge bg-body-tertiary border text-body">
                  {fmtMoney(transaction.sendAmount)} {transaction.sendCurrency}
                </span>
                <span className="mx-1">→</span>
                <span className="badge bg-body-tertiary border text-body">
                  {fmtMoney(transaction.receiveAmount)} {transaction.receiveCurrency}
                </span>
              </div>

              <small className="text-body-secondary">Sent {sentAt}</small>
            </div>
          </div>

          <div className="card-body p-3 p-md-4">

            <div className="card border shadow-sm rounded-4 mb-3 bg-body">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  {busy && <span className="text-body-secondary small">Processing…</span>}
                </div>
                <div className="mt-2">
                  <StatusSelector
                    value={status}
                    onChange={handleStatusRequest}
                    disabled={busy}
                    lockBackward={true}
                    showHint={true}
                  />
                </div>
              </div>
            </div>

            <div className="card border shadow-sm rounded-4 mb-3 bg-body">
              <div className="card-body">
                <TransferTraker transaction={{ ...transaction, status }} />
              </div>
            </div>

            <div className="card border shadow-sm rounded-4 mb-3 bg-body">
              <TransactionDetailsPanel
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                transaction={transaction}
                status={status}
                sentAt={sentAt}
                onDelete={handleDeleteRequest}
                onEditSubmit={handleEditSubmit}
                busy={busy}
                handleCopy={handleCopy}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
